import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOSchema from '@/components/base/SEOSchema';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该gay片文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-950 font-medium rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Get related articles (same category, excluding current)
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="Article"
        title={article.title}
        description={article.excerpt}
        url={`/article/${article.slug}`}
        datePublished={article.publishDate}
        authorName={article.author}
        articleBody={article.content}
        imageUrl={article.coverImage}
      />

      <Navbar />

      <main className="pt-16">
        {/* Article Header */}
        <section className="py-12 md:py-16 bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="flex items-center gap-2 mb-4 mt-2">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-accent-500/10 text-accent-500 text-xs font-medium rounded-md hover:bg-accent-500/20 transition-colors"
              >
                {article.categoryName}
              </Link>
              {article.featured && (
                <span className="px-3 py-1 bg-secondary-100 text-secondary-900 text-xs font-medium rounded-md">
                  精选文章
                </span>
              )}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </div>
                {article.author}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </div>
                {article.publishDate}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </div>
                {article.readTime}
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`text-sm ${
                        i < Math.floor(article.rating)
                          ? 'ri-star-fill text-accent-500'
                          : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  </div>
                ))}
                <span className="ml-1">{article.rating}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Cover */}
        <section className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <article
              className="prose prose-invert max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:md:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-800 prose-strong:font-semibold
                prose-a:text-accent-500 prose-a:hover:text-accent-400"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200">
              <h4 className="text-sm font-semibold text-foreground-700 mb-3">文章标签</h4>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-100 text-secondary-900 text-sm rounded-md"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-8">
                相关gay片文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-500/20 transition-all duration-300"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-accent-500 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back to Category */}
        <section className="py-10 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-700 font-medium rounded-lg hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回{article.categoryName}分类
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}